export abstract class TeoError<
  TErrorTag extends string = string,
  TErrorCodes extends string = string,
> {
  readonly _tag: TErrorTag;
  readonly code: TErrorCodes;
  name: TErrorTag;
  message: string;

  cause?: unknown;

  protected constructor(tag: TErrorTag, code: TErrorCodes, message: string, cause?: unknown) {
    this._tag = tag;
    this.name = tag;
    this.code = code;
    this.message = message;
    this.cause = cause;
  }

  static isTeoError(error: unknown): error is TeoError<string, string> {
    return (
      !!error &&
      typeof error === 'object' &&
      '_tag' in error &&
      'code' in error &&
      'message' in error
    );
  }

  static isError(error: unknown): error is TeoError<string, string> | Error {
    return isTeoError(error) || error instanceof Error;
  }

  static toString(error: unknown): string {
    if (TeoError.isTeoError(error)) {
      return `[${error.name}:${error.code}] ${error.message}`;
    }
    return error instanceof Error ? error.message : String(error);
  }
}

export function createTeoError<TErrorTag extends string, TErrorCodes extends string>(
  errorTag: TErrorTag,
  // just for type safety
  _errorCodes: TErrorCodes[] | readonly TErrorCodes[],
) {
  return class TeoErrorCreator extends TeoError<TErrorTag, TErrorCodes> {
    constructor(code: TErrorCodes, message: string, cause?: unknown) {
      super(errorTag, code, message, cause);
    }

    static matches<TErrorCode extends TErrorCodes = TErrorCodes>(
      error: unknown,
      code?: TErrorCode,
    ): error is TeoError<TErrorTag, TErrorCode> {
      return (
        isTeoError(error) && error._tag === errorTag && (code === undefined || error.code === code)
      );
    }
  };
}

export function isTeoError(error: unknown): error is TeoError<string, string> {
  return TeoError.isTeoError(error);
}

export function isError(error: unknown): error is TeoError<string, string> | Error {
  return TeoError.isError(error);
}
